import type { NewProjectResolution } from '@/model/buildDefaultMainProject'
import {
  listLocalStaticResourceMaps,
  resolveLocalStaticTemplateUrl,
  type LocalStaticTemplateItem,
} from '@/features/static-resource/localResourceMap'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'

export type BuiltinUiTemplate = {
  id: string
  label: string
  order: number
  sourceOuiName: string
  width: number
  height: number
  templateUrl: string
  previewUrl: string
  galleryUrls: string[]
}

function toBuiltinUiTemplate(resolutionKey: string, width: number, height: number, item: LocalStaticTemplateItem): BuiltinUiTemplate {
  return {
    id: item.id,
    label: item.label,
    order: item.order,
    sourceOuiName: item.sourceOuiName,
    width,
    height,
    templateUrl: resolveLocalStaticTemplateUrl(resolutionKey, item.ouiqPath),
    previewUrl: item.preview.url,
    galleryUrls: item.gallery.map((asset) => asset.url),
  }
}

const BUILTIN_TEMPLATES: BuiltinUiTemplate[] = listLocalStaticResourceMaps().flatMap((resourceMap) =>
  resourceMap.templates.map((template) =>
    toBuiltinUiTemplate(
      resourceMap.resolution.key,
      resourceMap.resolution.width,
      resourceMap.resolution.height,
      template,
    )),
)
const templateCache = new Map<string, Promise<Uint8Array>>()

export function listBuiltinUiTemplates(resolution?: NewProjectResolution): BuiltinUiTemplate[] {
  if (!resolution) return [...BUILTIN_TEMPLATES]
  return BUILTIN_TEMPLATES
    .filter((item) => item.width === resolution.width && item.height === resolution.height)
    .sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'zh-CN'))
}

export function getBuiltinUiTemplate(templateId: string): BuiltinUiTemplate | null {
  return BUILTIN_TEMPLATES.find((item) => item.id === templateId) ?? null
}

export async function loadBuiltinUiTemplateBytes(params: {
  templateId: string
  dataVersion: number
  onProgress?: (progress: number, status: string) => void
}): Promise<Uint8Array> {
  const template = getBuiltinUiTemplate(params.templateId)
  if (!template) {
    throw new Error(`未找到内置 ouiq 模板：${params.templateId}`)
  }

  const cacheKey = `${template.id}:${params.dataVersion}`
  const existing = templateCache.get(cacheKey)
  if (existing) return existing

  const task = (async () => {
    params.onProgress?.(5, '读取内置 ouiq 模板…')
    const response = await fetch(template.templateUrl)
    if (!response.ok) {
      throw new Error(`读取内置模板失败：HTTP ${response.status}`)
    }
    return await BrowserFileUtils.responseToUint8Array(response)
  })()

  templateCache.set(cacheKey, task)
  try {
    return await task
  } catch (error) {
    templateCache.delete(cacheKey)
    throw error
  }
}
